import { useLocation, useNavigate } from 'react-router-dom';
import { Logo, SidebarItem, Avatar, IconButton } from '../../ds';
import { NAV, NavIcon } from '../nav';
import type { User } from '../../lib/contract';

const groupLabel: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  padding: '18px 12px 6px',
};

interface Props {
  user: User;
  crewBadge?: number;
  reviewsBadge?: number;
  talkOpen?: boolean;
  onOpenTalk: () => void;
  onSignOut: () => void;
}

export function Sidebar({ user, crewBadge, reviewsBadge, talkOpen, onOpenTalk, onSignOut }: Props) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const surfaces = NAV.filter((n) => n.group === 'surfaces');
  const product = NAV.filter((n) => n.group === 'product');
  const badgeFor = (id: string) => (id === 'crew' ? crewBadge : id === 'reviews' ? reviewsBadge : undefined);

  return (
    <aside style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '18px 16px 12px' }}>
        <Logo size={25} glow />
        <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          // mission control
        </div>
      </div>

      <nav className="mc-scroll" aria-label="Primary" style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '8px 10px', overflow: 'auto', flex: 1 }}>
        <div style={{ ...groupLabel, paddingTop: 12 }}>// surfaces</div>
        {surfaces.map((n) => (
          <SidebarItem
            key={n.id}
            icon={<NavIcon id={n.id} />}
            label={n.label}
            active={pathname === n.path}
            badge={badgeFor(n.id)}
            onClick={() => nav(n.path)}
          />
        ))}

        <div style={groupLabel}>// product</div>
        {product.map((n) => (
          <SidebarItem
            key={n.id}
            icon={<NavIcon id={n.id} />}
            label={n.label}
            active={pathname === n.path}
            badge={badgeFor(n.id)}
            onClick={() => nav(n.path)}
          />
        ))}

        <div style={groupLabel}>// channel</div>
        <button
          onClick={onOpenTalk}
          aria-expanded={!!talkOpen}
          aria-controls="talk-dock"
          style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '9px 11px', borderRadius: 'var(--radius-md)', border: '1px solid transparent', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 14, textAlign: 'left' }}
        >
          <span style={{ display: 'flex', flex: 'none' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a8 8 0 0 1-11.5 7.2L3 21l1.8-6.5A8 8 0 1 1 21 12Z" />
            </svg>
          </span>
          <span style={{ flex: 1 }}>Talk to AI</span>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 7px var(--accent)' }} />
        </button>
      </nav>

      <div style={{ padding: 12, borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar name={user.name} tone="iris" status="online" size={34} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>{user.role}</div>
        </div>
        <IconButton variant="ghost" size="sm" label="Sign out" onClick={onSignOut}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
          </svg>
        </IconButton>
      </div>
    </aside>
  );
}
